import React from "react";
import { Controller, type Path, useFormContext } from "react-hook-form";
import FormField from "../../vendor/FormField.jsx";
import { Select as BaseSelect } from "../../vendor/Select/index.js";

export type SelectProps = {
  /**
   * Whether or the field can be cleared by the user.
   *
   * When `true`, the Select will render an icon that allows the user to clear the current selected
   * value. The icon does not appear when no value is selected.
   *
   * @default false
   */
  clearable?: boolean | undefined;

  /**
   * If `true`, the component is disabled.
   *
   * @default false
   */
  disabled?: boolean | undefined;

  /**
   * An error message to display below the element when in an invalid state.
   */
  error?: { message?: string | undefined } | undefined;

  /**
   * Explanatory text to render below the element.
   */
  helpText?: React.ReactNode | undefined;

  /**
   * A user-friendly label displayed alongside the element.
   */
  label?: string | undefined;

  /**
   * A unique name identifying the element within a form.
   */
  name?: string | undefined;

  /**
   * <!-- TODO(ndhoule): Document me -->
   */
  options: { label: string; value: string }[] | undefined;

  /**
   * Placeholder text displayed when no value is selected.
   */
  placeholder?: string | undefined;

  /**
   * When `true`, the element will be invalid when touched and no selection has been made.
   *
   * @default false
   */
  required?: boolean | undefined;
};

/**
 * Select components give users the ability to choose one or more options from a list of choices.
 *
 * <!-- TODO(ndhoule): Add a screenshot here -->
 *
 * @param props
 * @see {@link Form}
 * @example
 * ```tsx
 * import { Select } from "@netlify/sdk/react/components";
 *
 * <Select
 *   label="Your Favorite Dog"
 *   name="favoriteDog"
 *   options={[
 *     { label: "Corgi", value: "corgi" },
 *     { label: "Golden Retriever", value: "golden-retriever" },
 *     { label: "Pomeranian", value: "pomeranian" },
 *   ]}
 * />
 * ```
 */
export const Select = <
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  FieldValues extends Record<string, unknown>,
>({
  label,
  helpText,
  name,
  options,
  required,
  disabled,
  error,
  clearable: isClearable,
  ...otherProps
}: SelectProps) => {
  const { control } = useFormContext<FieldValues>();

  return (
    <Controller
      name={name as unknown as Path<FieldValues>}
      control={control}
      render={({ field, fieldState, formState }) => {
        return (
          <FormField
            disabled={disabled}
            error={error?.message ?? fieldState.error?.message}
            helpText={helpText}
            label={label}
            control={
              <BaseSelect
                {...field}
                valueOnly
                required={required}
                options={options}
                isClearable={isClearable}
                isDisabled={formState.disabled || disabled}
                {...otherProps}
              />
            }
          />
        );
      }}
    />
  );
};
